/**
 * Cache BACA untuk halaman tampilan utama saat sinyal lemah atau mati (Okt 2026).
 *
 * Tujuan: halaman utama (daftar order, detail order, workflow nakes, dashboard, dan chat: daftar chat,
 * info ruang beserta peserta, riwayat pesan) tetap terbuka dan menampilkan data terakhir yang pernah
 * dimuat, diberi label jelas bahwa itu data lama.
 *
 * Aturan keselamatan:
 *  - OPT-IN: hanya permintaan GET yang memberi `vmCache: true` yang disimpan atau dilayani dari
 *    salinan. Form edit, penugasan, verifikasi hasil tulis, dan semua pembacaan lain tidak
 *    memberi tanda itu, jadi tidak pernah menerima data lama.
 *  - Ditambah daftar putih bentuk URL (WHITELIST) sebagai pagar kedua.
 *  - Disimpan per user (user_id di token SAAT permintaan dikirim) di IndexedDB HP,
 *    kedaluwarsa 24 jam, maksimal 150 entri, dihapus saat logout, token dibuang, atau impersonate.
 *  - Salinan hanya dipakai kalau permintaan GAGAL tanpa jawaban server (sinyal mati / timeout /
 *    502 503 504 524). Jawaban server apa pun (401, 403, 404, 500, dst) tidak pernah diganti.
 *  - Permintaan TULIS (POST/PUT/PATCH/DELETE) ke /orders/ORDxxx menghapus salinan order itu.
 */

import axios from 'axios';

const DB_NAME = 'vm-read-cache';
const STORE = 'r';
export const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ENTRY_CHARS = 1500000;
const MAX_ENTRIES = 150;
const CACHE_AWARE_TIMEOUT_MS = 20000;
const IDB_WAIT_MS = 300;
const STALE_SHOW_MS = 30 * 60 * 1000;

// Pagar kedua: hanya bentuk URL ini (setelah /api).
const WHITELIST = [
  /^\/orders(\?.*)?$/,
  /^\/orders\/paginated(\?.*)?$/,
  /^\/orders\/ORD\d+(\?.*)?$/,
  /^\/orders\/ORD\d+\/workflow\/(service-confirmation|ic-status)(\?.*)?$/,
  /^\/nakes\/dashboard(\?.*)?$/,
  /^\/marketing\/dashboard(\?.*)?$/,
  /^\/marketing-teams\/my-team(\?.*)?$/,
  // Okt 2026: chat. Daftar chat, info ruang (termasuk peserta), dan riwayat pesan yang pernah dibuka.
  /^\/job-discussions(\?.*)?$/,
  /^\/job-discussions\/list(\?.*)?$/,
  /^\/job-discussions\/order\/[^/?#]+\/rooms(\?.*)?$/,
  /^\/job-discussions\/[^/?#]+\/messages(\?.*)?$/,
  /^\/nakes-chats(\?.*)?$/,
  /^\/nakes-chats\/[^/?#]+\/(details|messages)(\?.*)?$/,
  /^\/supply-requests\/discussions\/threads-with-messages(\?.*)?$/,
  /^\/logistic-returns\/discussions\/nakes-chat-list(\?.*)?$/,
  /^\/coordination-channels(\?.*)?$/,
];

// Chat dipolling tiap 3 sampai 5 detik. Isi yang sama dengan simpanan terakhir tidak ditulis ulang
// (hemat baterai dan memori HP); simpanan hanya disegarkan paling sering tiap 5 menit supaya
// batas 24 jam dihitung dari terakhir dilihat, bukan terakhir berubah.
const REWRITE_AFTER_MS = 5 * 60 * 1000;
const MAX_LAST_WRITES = 60;
const lastWrites = new Map(); // key -> { sig, at }
const signatureOf = (json) => {
  let h = 5381;
  for (let i = 0; i < json.length; i += 1) h = ((h * 33) ^ json.charCodeAt(i)) >>> 0;
  return `${json.length}:${h}`;
};

let installed = null;
let dbPromise = null;
let generation = 0; // naik setiap clearReadCache: permintaan lama tidak boleh menulis ulang
const staleKeys = new Map(); // key -> waktu salinan
const staleTimers = new Map();
let writeSeq = 0; // naik setiap ada tulis: GET yang mulai sebelum tulis tidak boleh menyimpan hasilnya
const listeners = new Set();

export const getStaleAt = () => {
  let t = null;
  staleKeys.forEach((v) => { if (t === null || v < t) t = v; });
  return t;
};
export const subscribeStale = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const notifyStale = (reason) => {
  const t = getStaleAt();
  listeners.forEach((fn) => { try { fn(t, reason); } catch { /* abaikan */ } });
};
const markStale = (key, t) => {
  staleKeys.set(key, t);
  if (staleTimers.has(key)) clearTimeout(staleTimers.get(key));
  staleTimers.set(key, setTimeout(() => {
    staleTimers.delete(key);
    if (staleKeys.delete(key)) notifyStale('expired');
  }, STALE_SHOW_MS));
  notifyStale('set');
};
const clearStale = (key) => {
  if (staleTimers.has(key)) { clearTimeout(staleTimers.get(key)); staleTimers.delete(key); }
  if (staleKeys.delete(key)) notifyStale(staleKeys.size === 0 ? 'recovered' : 'partial');
};

const apiPath = (url) => {
  try {
    const u = new URL(String(url), window.location.origin);
    const i = u.pathname.indexOf('/api/');
    if (i === -1) return null;
    return u.pathname.slice(i + 4) + u.search;
  } catch {
    return null;
  }
};

export const isCacheable = (url) => {
  const p = apiPath(url);
  return !!p && WHITELIST.some((re) => re.test(p));
};

const currentUserId = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64)).user_id || null;
  } catch {
    return null;
  }
};

const keyOf = (uid, config) => {
  const p = apiPath(axios.getUri(config));
  if (!uid || !p) return null;
  return `${uid}|${p}`;
};

const withTimeout = (promise, ms, fallback) => new Promise((resolve) => {
  const t = setTimeout(() => resolve(fallback), ms);
  promise.then((v) => { clearTimeout(t); resolve(v); }, () => { clearTimeout(t); resolve(fallback); });
});

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => {
        const db = req.result;
        db.onversionchange = () => { try { db.close(); } catch { /* abaikan */ } dbPromise = null; };
        db.onclose = () => { dbPromise = null; };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
};

const tx = async (mode, fn) => {
  const db = await openDb();
  if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const t = db.transaction(STORE, mode);
      const store = t.objectStore(STORE);
      const r = fn(store);
      t.oncomplete = () => resolve(r && 'result' in r ? r.result : undefined);
      t.onerror = () => resolve(undefined);
      t.onabort = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
};

const getEntry = async (key) => {
  const e = await tx('readonly', (s) => s.get(key));
  if (!e || typeof e.t !== 'number') return null;
  if (Date.now() - e.t > CACHE_TTL_MS) return null;
  return e;
};

const putEntry = (key, entry) => tx('readwrite', (s) => s.put(entry, key));

/** Hapus semua salinan (logout / token dibuang / impersonate). */
export const clearReadCache = () => {
  generation += 1;
  lastWrites.clear();
  staleKeys.clear();
  staleTimers.forEach((t) => clearTimeout(t));
  staleTimers.clear();
  notifyStale('clear');
  return tx('readwrite', (s) => s.clear());
};

/** Hapus salinan order itu, semua daftar order, dan dashboard setelah ada penulisan ke order. */
const dropOrder = async (orderId) => {
  lastWrites.clear(); // salinan dihapus: tulisan berikutnya tidak boleh dilewati sebagai "sama"
  const db = await withTimeout(openDb(), IDB_WAIT_MS, null);
  if (!db) return;
  try {
    const t = db.transaction(STORE, 'readwrite');
    const store = t.objectStore(STORE);
    const cur = store.openKeyCursor();
    cur.onsuccess = () => {
      const c = cur.result;
      if (!c) return;
      const k = String(c.key);
      if (
        k.includes(`/orders/${orderId}`)
        || /\|\/orders(\?|\/paginated|$)/.test(k)
        || /\|\/(nakes|marketing)\/dashboard/.test(k)
      ) store.delete(c.primaryKey);
      c.continue();
    };
  } catch { /* abaikan */ }
};

const housekeeping = async () => {
  const db = await openDb();
  if (!db) return;
  try {
    // Baca saja dulu (tidak mengunci pembacaan lain), baru hapus yang perlu.
    const all = [];
    const t = db.transaction(STORE, 'readonly');
    const cur = t.objectStore(STORE).openCursor();
    cur.onsuccess = () => {
      const c = cur.result;
      if (c) {
        all.push({ k: c.primaryKey, t: c.value && c.value.t });
        c.continue();
      }
    };
    t.oncomplete = () => {
      const now = Date.now();
      const dead = all.filter((x) => typeof x.t !== 'number' || now - x.t > CACHE_TTL_MS).map((x) => x.k);
      const alive = all.filter((x) => typeof x.t === 'number' && now - x.t <= CACHE_TTL_MS).sort((a, b) => a.t - b.t);
      if (alive.length > MAX_ENTRIES) alive.slice(0, alive.length - MAX_ENTRIES).forEach((x) => dead.push(x.k));
      if (!dead.length) return;
      const w = db.transaction(STORE, 'readwrite').objectStore(STORE);
      dead.forEach((k) => w.delete(k));
    };
  } catch { /* abaikan */ }
};

const isNoReply = (error) => {
  if (!axios.isAxiosError(error) || axios.isCancel(error) || error.code === 'ERR_CANCELED') return false;
  if (!error.response) return true;
  return [502, 503, 504, 524].includes(error.response.status);
};

// vmCache: true = simpan dan boleh dilayani dari salinan; 'store' = hanya simpan (pembaruan setelah aksi).
const wantsCache = (config) => (
  !!config && (config.vmCache === true || config.vmCache === 'store') && !config.vmNoCache
  && (!config.responseType || config.responseType === 'json')
  && isCacheable(axios.getUri(config))
);

export function installReadCache() {
  if (installed) return installed;

  const reqId = axios.interceptors.request.use(async (config) => {
    const method = String(config.method || 'get').toLowerCase();
    if (method !== 'get') {
      const m = /\/orders\/(ORD\d+)/.exec(String(config.url || ''));
      if (m) { writeSeq += 1; dropOrder(m[1]); }
      else if (/\/orders(\?|$)/.test(String(config.url || '').split('/api')[1] || '')) { writeSeq += 1; dropOrder('__none__'); }
      return config;
    }
    if (!wantsCache(config)) return config;
    config.__vmUid = currentUserId();
    config.__vmGen = generation;
    config.__vmSeq = writeSeq;
    // Kalau sudah ada salinan, batasi tunggu 20 detik (bukan 30 atau lebih) supaya halaman tetap terbuka.
    if (config.vmCache === true && !config.timeout && config.__vmUid) {
      const key = keyOf(config.__vmUid, config);
      const entry = key ? await withTimeout(getEntry(key), IDB_WAIT_MS, null) : null;
      if (entry) config.timeout = CACHE_AWARE_TIMEOUT_MS;
    }
    return config;
  });

  const resId = axios.interceptors.response.use(
    (response) => {
      try {
        const cfg = response && response.config;
        if (cfg && cfg.__vmUid && !cfg.__vmFromCache && wantsCache(cfg)) {
          const key = keyOf(cfg.__vmUid, cfg);
          if (key) {
            clearStale(key);
            if (response.status === 200 && cfg.__vmGen === generation && cfg.__vmSeq === writeSeq && currentUserId() === cfg.__vmUid) {
              const json = JSON.stringify(response.data == null ? null : response.data);
              if (json.length <= MAX_ENTRY_CHARS) {
                const sig = signatureOf(json);
                const now = Date.now();
                const prev = lastWrites.get(key);
                if (!prev || prev.sig !== sig || now - prev.at > REWRITE_AFTER_MS) {
                  // Catat "sudah tersimpan" hanya setelah penulisan ke HP benar-benar berhasil.
                  // Kalau gagal (penyimpanan penuh dll), isi yang sama tetap dicoba lagi di polling berikutnya.
                  const gen = generation;
                  Promise.resolve(putEntry(key, { t: now, data: response.data })).then((saved) => {
                    if (saved === undefined || gen !== generation) return;
                    lastWrites.delete(key);
                    lastWrites.set(key, { sig, at: now });
                    if (lastWrites.size > MAX_LAST_WRITES) lastWrites.delete(lastWrites.keys().next().value);
                  });
                }
              }
            }
          }
        }
      } catch { /* cache tidak boleh mengubah hasil permintaan */ }
      return response;
    },
    async (error) => {
      try {
        const cfg = error && error.config;
        if (cfg && cfg.__vmUid && cfg.vmCache === true && isNoReply(error) && wantsCache(cfg) && currentUserId() === cfg.__vmUid) {
          const key = keyOf(cfg.__vmUid, cfg);
          const entry = key ? await withTimeout(getEntry(key), IDB_WAIT_MS * 3, null) : null;
          if (entry && cfg.__vmGen === generation) {
            cfg.__vmFromCache = true;
            markStale(key, entry.t);
            return {
              data: entry.data, status: 200, statusText: 'OK (data lama)', headers: {},
              config: cfg, request: error.request, __vmFromCache: true, __vmCachedAt: entry.t,
            };
          }
        }
      } catch { /* jatuh ke error asli */ }
      return Promise.reject(error);
    }
  );

  // Tanpa token (sudah logout / sesi bersih): tidak boleh ada sisa data di HP.
  if (!currentUserId()) clearReadCache();
  setTimeout(housekeeping, 10000);

  installed = () => {
    axios.interceptors.request.eject(reqId);
    axios.interceptors.response.eject(resId);
    installed = null;
  };
  return installed;
}
